import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import moment from 'moment';

import { StatusEnum } from '@shared/constants/site_status.enum';
import { DialogEligibilityQuestionComponent } from '@shared/dialogs/dialog-eligibility-question/dialog-eligibility-question.component';
import { Parcel, PaymentPeriod, Site } from '@shared/models';
import * as fromApp from '@store';
import { EnrollmentGroupService } from '../api/enrollment-group.service';
import { SiteService } from '../api/site.service';
import { Observable, ReplaySubject } from "rxjs";
import { FormatHelperService } from './format-helper.service';
import { SiteStatusService } from './site-status.service';
import { DialogFormComponent } from '@shared/dialogs/dialog-form/dialog-form.component';
import { FormControl } from '@angular/forms';
import { ToastService } from '@shared/services/toast.service';
import { setActiveSite } from '@store/site/site.actions';

@Injectable({
  providedIn: 'root',
})
export class SiteHelperService {
  readonly ready$ = new ReplaySubject<void>(1);

  constructor(
    public dialog: MatDialog,
    readonly router: Router,
    readonly siteService: SiteService,
    readonly toastService: ToastService,
    readonly siteStatusService: SiteStatusService,
    readonly formatHelper: FormatHelperService,
    readonly enrollmentGroupService: EnrollmentGroupService,
    readonly store: Store<fromApp.AppState>
  ) {

  }

  readOnlyMapCheck(info: { siteStatus: string; isAdmin: boolean; isLandowner: boolean }): boolean {
    if (info.isLandowner) {
      return !this.siteStatusService.sitePendingStatusList.includes(info.siteStatus);
    }
    return !(
      this.siteStatusService.sitePendingStatusList.includes(info.siteStatus) || this.siteStatusService.siteUnderReviewStatusList.includes(info.siteStatus)
    );
  }

  canSaveMap(info: { siteStatus: string; isAdmin: boolean; isLandowner: boolean }): boolean {
    return !this.readOnlyMapCheck(info) && (info.isAdmin || info.isLandowner);
  }

  showFirewoodDialog(deductionPerYear: string) {
    return this.dialog.open(DialogEligibilityQuestionComponent, {
      panelClass: 'fc-dialog-eligibility-question',
      data: {
        title: 'ARE YOU SURE?',
        question:
          'By selecting YES to cutting up to 5 cords of dead or dying trees for firewood use in the coming year,',
        redText: ' you agree that your annual payment will be adjusted for the coming year:',
        infoBox:
          "A cord is defined as 128 cubic feet of wood. This measures approximately 4' high x 8' long x 4' deep.",
        deductionPerYear,
        buttonTextYes: 'Yes, I Agree',
        buttonTextNo: 'No, Cancel',
      },
      autoFocus: false,
    });
  }

  showClearingDialog(allowedClearing: string, deductionPerYear: string) {
    return this.dialog.open(DialogEligibilityQuestionComponent, {
      panelClass: 'fc-dialog-eligibility-question',
      data: {
        title: 'ARE YOU SURE?',
        question: `You are exercising the <b>one-time option</b> to clear up to ${allowedClearing} acres of land in the coming year`,
        redText: ` and you agree that your annual payment will be <b>permanently</b> adjusted:`,
        deductionPerYear: deductionPerYear,
        buttonTextYes: 'Yes, I Agree',
        buttonTextNo: 'No, Cancel',
      },
      autoFocus: false,
    });
  }

  getPayablePaymentPeriods(sites: Site[]): PaymentPeriod[] {
    let payablePaymentPeriods = [];
    if (sites) {
      // get a list of all the sites' payment periods that are
      // both in the past and have a report
      for (const site of sites) {
        if (site.paymentPeriods) {
          site.paymentPeriods.forEach(period => {
            // Check if the combination already exists in uniqueCombinations
            if ((period.reportId != null || period.reportResponseId != null) && moment(period.endDate)
              .isBefore(moment())) {
              const exists = payablePaymentPeriods.some(
                combination => combination.startDate === period.startDate && combination.endDate === period.endDate
              );

              // If not, add it to uniqueCombinations
              if (!exists) {
                let clonePeriod = cloneDeep(period);
                clonePeriod.id = payablePaymentPeriods.length + 1;
                payablePaymentPeriods.push(clonePeriod);
              }
            }
          });
        }
      }
    }
    return payablePaymentPeriods;
  }

  sitesArePayable(sites: Site[]): boolean {
    let accountsPayable = false;
    let isActive = false;

    if (sites) {
      // the sites must have been activated
      isActive = sites.every((val) => val.activationDate !== null);
      // make sure the users are all able to be paid via Stripe
      accountsPayable = sites.every(
        (val) =>
          val.applicant.stripeAccounts.filter(
            sa =>
              sa.stripeAccountCreationDate !== null &&
              val.status === StatusEnum.PAYMENT_READY
          ).length > 0
      );
    }
    return accountsPayable && isActive;
  }

  whenReady(): Observable<void> {
    return this.ready$;
  }

  updateParcelMetadata(site: Site, parcel: Parcel) {
    this.dialog.open(DialogFormComponent, {
      data: {
        title: "Edit Parcel Information",
        fields: {
          parcelNumber: {
            label: "Parcel Number",
            inputType: "string",
            control: new FormControl(parcel.parcelNumber)
          },
          owner: {
            label: "Owner Name",
            inputType: "string",
            control: new FormControl(parcel.owner)
          },
          address: {
            label: "Parcel Address",
            inputType: "string",
            control: new FormControl(parcel.address)
          },
          county: {
            label: "County",
            inputType: "string",
            control: new FormControl(parcel.county)
          },
          state: {
            label: "State",
            inputType: "string",
            control: new FormControl(parcel.state)
          }
        }
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.siteService
          .updateParcelData(
            site.id,
            parcel.id,
            result.owner,
            result.address,
            result.parcelNumber,
            result.county,
            result.state
          )
          .subscribe(() => {
            this.store.dispatch(setActiveSite({ id: site.id }));
          });
      }
    });
  }
}
